import { Leaf, Beaker, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MetricCard } from '../primitives'

interface FunctionalUnit {
  quantity: number
  unit: string
  activity_name?: string
}

interface ActivityInfo {
  code: string
  name: string
  unit: string
  location: string
}

interface CalculationResultData {
  lca_score: number
  score_unit: string
  functional_unit?: FunctionalUnit
  activities?: ActivityInfo
  method?: string
  database?: string
  calculation_type?: string
  calculation_time_seconds?: number
  task_id?: string
  status?: string
  success?: boolean
}

interface CalculationResultRendererProps {
  data: CalculationResultData
}

export function CalculationResultRenderer({ data }: CalculationResultRendererProps) {
  // If no lca_score, this isn't a completed calculation — show as generic data
  // This happens when calculate_standard returns a queued task or pending status
  if (data.lca_score === undefined || data.lca_score === null) {
    return null // Handled by resolveRenderer fallback
  }

  // Parse method string if present
  const methodParts = data.method?.split(' > ') || []
  const methodCategory = methodParts[1] || ''
  const methodIndicator = methodParts[3] || methodParts[2] || ''

  // Format the score nicely
  const formatScore = (score: number): string => {
    if (Math.abs(score) < 0.001 && score !== 0) {
      return score.toExponential(3)
    }
    return score.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    })
  }

  return (
    <div className="space-y-3">
      {/* Main score - hero display */}
      <div
        className={cn('rounded-lg border p-4 text-center', 'bg-success-muted/30 border-success/30')}
      >
        <div className="flex items-center justify-center gap-2 mb-1">
          <Leaf className="w-5 h-5 text-success" />
          <span className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
            LCA Score
          </span>
        </div>
        <div className="flex items-baseline justify-center gap-2">
          <span className="text-3xl font-bold text-success">{formatScore(data.lca_score)}</span>
          <span className="text-sm text-muted-foreground">{data.score_unit}</span>
        </div>
        {methodIndicator && (
          <div className="text-xs text-muted-foreground mt-1">{methodIndicator}</div>
        )}
      </div>

      {/* Functional unit */}
      {data.functional_unit && (
        <div className="rounded-md border border-border bg-muted/20 p-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-1">
            Functional Unit
          </div>
          <div className="text-sm">
            <span className="font-semibold">{data.functional_unit.quantity}</span>{' '}
            <span className="text-muted-foreground">{data.functional_unit.unit}</span>
            {data.functional_unit.activity_name && (
              <span className="text-muted-foreground">
                {' '}
                of {data.functional_unit.activity_name}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Activity details */}
      {data.activities && (
        <div className="rounded-md border border-border bg-muted/20 p-2 space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
            Activity
          </div>
          <div className="text-sm font-medium line-clamp-2">{data.activities.name}</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {data.activities.location}
            </span>
            <span className="inline-flex items-center gap-1">
              <Beaker className="w-3 h-3" />
              {data.activities.unit}
            </span>
          </div>
        </div>
      )}

      {/* Method info */}
      {methodCategory && (
        <div className="flex flex-wrap gap-2">
          <MetricCard label="Method" value={methodCategory} size="sm" />
          {data.database && (
            <MetricCard
              label="Database"
              value={data.database.replace('ecoinvent-', '').replace('-cutoff', '')}
              size="sm"
            />
          )}
          {data.calculation_time_seconds && (
            <MetricCard
              label="Calc Time"
              value={data.calculation_time_seconds.toFixed(2)}
              unit="s"
              size="sm"
            />
          )}
        </div>
      )}
    </div>
  )
}
